import Link from 'next/link';
import { NextSeo } from 'next-seo';

import Footer from '@/components/layouts/Footer';
import Header from '@/components/layouts/Header';
import { productCapabilities, productFacts } from '@/content/productFacts';

export default function ProductFactsPage() {
  return (
    <>
      <NextSeo
        title="Product Facts — AI Knowledge Agents"
        description={productFacts.description}
        canonical={`${productFacts.url}/product`}
      />
      <Header />
      <main className="mx-auto max-w-4xl px-6 py-20 text-slate-800">
        <p className="text-sm font-semibold uppercase tracking-wider text-primary-600">
          Chat.co product facts
        </p>
        <h1 className="mt-4 text-4xl font-bold">AI knowledge agents with source citations</h1>
        <p className="mt-6 text-xl leading-relaxed">{productFacts.description}</p>
        <p className="mt-4 text-sm text-slate-500">
          Reviewed September 24, 2026 · Maintained by the Chat.co product team
        </p>
        <section className="my-10 rounded-2xl bg-slate-50 p-6">
          <h2 className="text-2xl font-semibold">Trial and paid plans</h2>
          <p className="mt-4">{productFacts.trial}</p>
          <p className="mt-3">
            Starter, Pro, Business, and Enterprise have different quotas and model access. Optional
            add-ons expand selected limits and features.
          </p>
          <Link
            className="mt-4 inline-block text-primary-600 underline"
            href={productFacts.pricingUrl}>
            View current monthly and annual prices and plan limits
          </Link>
        </section>
        <h2 className="text-2xl font-semibold">Capabilities and limitations</h2>
        <dl className="mt-6 divide-y divide-slate-200">
          {productCapabilities.map((fact) => (
            <div key={fact.name} className="py-6">
              <dt className="text-lg font-semibold">{fact.name}</dt>
              <dd className="mt-2 leading-relaxed">
                {fact.detail}{' '}
                <Link href={fact.href} className="text-primary-600 underline">
                  Read {fact.name.toLowerCase()} details
                </Link>
                .
              </dd>
            </div>
          ))}
        </dl>
        <section className="mt-10">
          <h2 className="text-2xl font-semibold">Choose a workflow</h2>
          <ul className="mt-4 space-y-3 text-primary-600 underline">
            <li>
              <Link href="/ai-chatbot-for-website">Website agents for customer questions</Link>
            </li>
            <li>
              <Link href="/customer-support-ai-agent">Customer support and lead capture</Link>
            </li>
            <li>
              <Link href="/internal-ai-knowledge-base">Private company knowledge</Link>
            </li>
            <li>
              <Link href="/schools">School faculty knowledge and parent questions</Link>
            </li>
            <li>
              <Link href="/solutions/energy">Energy and regulatory research</Link>
            </li>
          </ul>
        </section>
        <section className="mt-10">
          <h2 className="text-2xl font-semibold">Customer evidence</h2>
          <p className="mt-4">
            <Link href="/case-studies/maxeta-energy" className="text-primary-600 underline">
              MAXeta Energy
            </Link>{' '}
            uses specialist agents for regulatory research.{' '}
            <Link href="/case-studies/calvario-academy" className="text-primary-600 underline">
              Calvario City Christian Academy
            </Link>{' '}
            uses a faculty portal for school knowledge. Customer results depend on source quality
            and deployment; they are not universal performance guarantees.
          </p>
        </section>
        <section className="mt-10">
          <h2 className="text-2xl font-semibold">Company and contact</h2>
          <p className="mt-4">
            Chat.co is operated by SA CYBER LLC. Contact{' '}
            <a href="mailto:support@chat.co" className="text-primary-600 underline">
              support@chat.co
            </a>{' '}
            or{' '}
            <Link href="/contact-us" className="text-primary-600 underline">
              book a demo
            </Link>{' '}
            to confirm requirements for your deployment.
          </p>
          <p className="mt-3">
            Read the{' '}
            <Link href="/privacy-policy" className="text-primary-600 underline">
              privacy policy
            </Link>{' '}
            and{' '}
            <Link href="/terms-condition" className="text-primary-600 underline">
              terms
            </Link>{' '}
            for contractual details.
          </p>
        </section>
      </main>
      <Footer />
    </>
  );
}
